/**
 * /register — refonte « bonnes affaires » du 2026-09-14.
 *
 * Le bandeau dit ce que le compte gratuit donne vraiment, sans promesse de
 * plus. Critères saisis avant l'inscription réappliqués au compte, connexion
 * immédiate.
 *
 * 2026-10-22 — le compte gratuit est le seul verrou (lien et source) :
 *   - deux champs, email et mot de passe. Le nom et la saisie d'un code
 *     parrain sont retirés ; un code arrivé par lien (?ref=) reste transmis ;
 *   - case de consentement aux emails, NON pré-cochée, tracée côté serveur
 *     avec son texte exact (CONSENTEMENT_EMAIL_TEXTE, backend auth.py) ;
 *   - retour sur la page demandée (?suivant=), sinon sur les bonnes affaires.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { login, register } from '../services/auth';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Page from '../components/Layout/Page';
import BandeauAffaires from '../components/Affaires/BandeauAffaires';
import { ecrireCriteres } from '../components/Affaires/criteres';
import { destinationApresConnexion, pageSuivante } from './LoginPage';
import './AuthPages.css';

const FORCES = ['', 'Faible', 'Moyen', 'Fort'];

export default function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [consentement, setConsentement] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const profil = searchParams.get('profil');
  const suivant = pageSuivante(searchParams);

  useEffect(() => {
    if (refCode) api.post(`/api/affiliate/track/${refCode}`).catch(() => {});
  }, [refCode]);

  const modifier = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const force = form.password.length === 0 ? 0 : form.password.length < 6 ? 1 : form.password.length < 10 ? 2 : 3;

  /* Critères saisis AVANT l'inscription (parcours guidé, teaser ville+budget) :
     on les récupère pour ne rien redemander après. */
  function critereEnregistres() {
    let prefs = {};
    try { prefs = JSON.parse(localStorage.getItem('zk_prefs') || '{}') || {}; } catch { prefs = {}; }
    const city = searchParams.get('city');
    const budget = searchParams.get('budget');
    if (city) prefs.location = city;
    if (budget) prefs.price_max = Number(budget);
    return prefs;
  }

  async function appliquerCriteres(prefs) {
    if (!prefs || (!prefs.price_max && !prefs.location)) return;
    try {
      await api.post('/api/particulier/onboarding', {
        budget_max: prefs.price_max || 999999,
        city: prefs.location || null,
        radius_km: prefs.radius || 50,
        fuel_type: prefs.fuel_type || null,
        gearbox: prefs.gearbox || null,
      });
    } catch { /* le profil reste modifiable depuis le compte */ }
  }

  function destination(profileType, prefs) {
    if (suivant) return suivant;
    if (profileType === 'garage') return '/garage';
    const criteres = ecrireCriteres({ prix_max: prefs.price_max ? String(prefs.price_max) : '' });
    return criteres ? `/explore?${criteres}` : destinationApresConnexion(profileType);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    setLoading(true);
    try {
      await register(form.email, form.password, refCode, profil, consentement);
      const tokenData = await login(form.email, form.password);
      signIn(tokenData.user);
      const prefs = critereEnregistres();
      await appliquerCriteres(prefs);
      navigate(destination(tokenData.user?.profile_type, prefs));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création du compte.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Page
      largeur="etroite"
      titre="Créer un compte gratuit"
      chapeau="Le lien et la source de chaque bonne affaire, avec un email et un mot de passe."
    >
      <div className="zk-carte zk-auth-carte">
        <ul className="zk-auth-atouts">
          <li>Le lien et le site d’origine de chaque annonce</li>
          <li>Favoris et historique de vos recherches</li>
          <li>Estimation de votre voiture et offres de garages</li>
        </ul>
        <form onSubmit={handleSubmit}>
          <div className="zk-champ">
            <label htmlFor="reg-email">Adresse email</label>
            <input id="reg-email" type="email" name="email" autoComplete="email" required placeholder="vous@email.com"
              value={form.email} onChange={modifier} />
          </div>
          <div className="zk-champ">
            <label htmlFor="reg-mdp">Mot de passe (8 caractères minimum)</label>
            <div className="zk-auth-mdp">
              <input id="reg-mdp" type={showPass ? 'text' : 'password'} name="password" autoComplete="new-password"
                required placeholder="••••••••" value={form.password} onChange={modifier} />
              <button type="button" className="zk-auth-voir" onClick={() => setShowPass((v) => !v)}
                aria-label={showPass ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                {showPass ? 'Masquer' : 'Afficher'}
              </button>
            </div>
            {force > 0 && (
              <>
                <div className={`zk-auth-jauge n${force}`} aria-hidden="true"><span /><span /><span /></div>
                <span className="zk-auth-force">{FORCES[force]}</span>
              </>
            )}
          </div>
          <label className="zk-auth-consentement">
            <input type="checkbox" checked={consentement} onChange={(e) => setConsentement(e.target.checked)} />
            <span>J’accepte de recevoir par email des alertes et les nouvelles bonnes affaires. Je peux me désinscrire à tout moment.</span>
          </label>
          {error && <div role="alert" className="zk-alerte erreur" style={{ marginBottom: 14 }}>{error}</div>}
          <button type="submit" className="zk-bouton plein" disabled={loading}>
            {loading ? 'Création…' : 'Créer mon compte'}
          </button>
        </form>
        <p className="zk-auth-aide">
          En créant un compte, vous acceptez nos <Link to="/cgu" className="zk-lien">conditions d’utilisation</Link>.
          Votre email sert à votre compte ; il n’est utilisé pour des envois que si vous cochez la case
          (<Link to="/confidentialite" className="zk-lien">confidentialité</Link>).
        </p>
        <div className="zk-auth-liens">
          <span>Déjà un compte ?</span>
          <Link to={suivant ? `/login?suivant=${encodeURIComponent(suivant)}` : '/login'} className="zk-lien">Se connecter →</Link>
        </div>
      </div>

      <BandeauAffaires titre="Ce que vous allez suivre" />
    </Page>
  );
}
